if (!customElements.get('fere-ring-gallery')) {
  customElements.define('fere-ring-gallery', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.photos = JSON.parse(this.querySelector('[data-gallery-data]').textContent);
      this.scope = this.closest('product-info');
      this.picture = this.querySelector('[data-gallery-image]');
      this.dialog = this.querySelector('dialog');
      this.onChange = () => requestAnimationFrame(() => this.syncColor());
      this.scope.addEventListener('change', this.onChange);
      this.addEventListener('click', (event) => {
        const view = event.target.closest('[data-gallery-view]');
        const color = event.target.closest('[data-gallery-color]');
        if (view) this.render(this.dataset.color, view.dataset.galleryView);
        if (color) this.selectColor(color.dataset.galleryColor);
        if (event.target.closest('[data-gallery-zoom]')) this.openZoom();
        if (event.target.closest('[data-gallery-close]') || event.target === this.dialog) this.dialog.close();
      });
      this.dialog.addEventListener('close', () => this.querySelector('[data-gallery-zoom]').focus({ preventScroll: true }));
      if (typeof subscribe === 'function') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (data.sectionId === this.scope.dataset.section) this.syncColor();
        });
      }
      this.render(this.dataset.color, this.dataset.view);
      this.syncColor();
    }

    disconnectedCallback() {
      this.scope?.removeEventListener('change', this.onChange);
      this.unsubscribe?.();
      this.initialized = false;
    }

    selectedMetal() {
      const picker = this.scope.querySelector('variant-selects, variant-radios');
      if (!picker) return '';
      const values = [...picker.querySelectorAll('input:checked, select')].map((input) => input.value);
      return values.find((value) => /(?:White|Yellow|Rose) Gold/i.test(value)) || '';
    }

    syncColor() {
      const color = this.selectedMetal().match(/(White|Yellow|Rose) Gold/i)?.[1].toLowerCase();
      if (color && color !== this.dataset.color) this.render(color, this.dataset.view);
    }

    selectColor(color) {
      if (!this.photos[color]) return;
      const metal = this.selectedMetal();
      const nextMetal = metal.replace(/White|Yellow|Rose/i, color[0].toUpperCase() + color.slice(1));
      const picker = this.scope.querySelector('variant-selects, variant-radios');
      if (!picker) return;
      const radio = [...picker.querySelectorAll('input[type="radio"]')].find((input) => input.value === nextMetal && !input.disabled);
      const select = [...picker.querySelectorAll('select')].find((input) => [...input.options].some((option) => option.value === nextMetal && !option.disabled));
      if (radio) {
        if (!radio.checked) radio.click();
        this.render(color, this.dataset.view);
      } else if (select) {
        select.value = nextMetal;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        this.render(color, this.dataset.view);
      }
    }

    sized(url, width) {
      const result = new URL(url, document.baseURI);
      const approvedAsset = /-oct10-[0-9a-f]{8}(?:_\d+x)?\.png$/i;
      if (approvedAsset.test(result.pathname)) {
        result.pathname = result.pathname.replace(/(-oct10-[0-9a-f]{8})(?:_\d+x)?(\.png)$/i, `$1_${width}x$2`);
        result.searchParams.delete('width');
      } else {
        result.searchParams.set('width', width);
      }
      return result.href;
    }

    resolvePhoto(color, view, allowViewFallback = true) {
      if (this.photos[color]?.[view]) {
        return { source: this.photos[color][view], picturedColor: color, view };
      }

      const referenceColor = Object.keys(this.photos).find((key) => this.photos[key]?.[view]);
      if (referenceColor) {
        return { source: this.photos[referenceColor][view], picturedColor: referenceColor, view };
      }

      if (allowViewFallback) {
        if (this.photos[color]?.front) {
          return { source: this.photos[color].front, picturedColor: color, view: 'front' };
        }
        const fallbackColor = Object.keys(this.photos).find((key) => this.photos[key]?.front);
        if (fallbackColor) {
          return { source: this.photos[fallbackColor].front, picturedColor: fallbackColor, view: 'front' };
        }
      }

      return { source: '', picturedColor: color, view };
    }

    render(color, view) {
      const resolved = this.resolvePhoto(color, view);
      const source = resolved.source;
      const picturedColor = resolved.picturedColor;
      view = resolved.view;
      const isReference = Boolean(source) && picturedColor !== color;
      this.dataset.picturedColor = picturedColor;
      this.dataset.photoReference = String(isReference);
      this.dataset.color = color;
      this.dataset.view = view;
      const metal = color[0].toUpperCase() + color.slice(1) + ' Gold';
      const picturedMetal = picturedColor[0].toUpperCase() + picturedColor.slice(1) + ' Gold';
      const label = ({ front: 'front view', face: 'hand beside face', flat: 'hand resting flat' })[view] || 'front view';
      this.dataset.photoUnavailable = String(!source);
      this.picture.hidden = !source;
      if (source) this.picture.src = this.sized(source, 1000);
      else this.picture.removeAttribute('src');
      const zoom = this.querySelector('[data-gallery-zoom]');
      zoom.hidden = !source;
      zoom.disabled = !source;
      const unavailable = this.querySelector('[data-gallery-unavailable]');
      unavailable.hidden = Boolean(source);
      unavailable.textContent = `Photo unavailable in ${metal}.`;
      this.picture.alt = `${this.dataset.title} in ${picturedMetal}, ${label}${isReference ? `; selected metal: ${metal}` : ''}`;
      this.querySelector('[data-gallery-caption]').textContent = isReference ? `Selected: ${metal} · Pictured: ${picturedMetal}` : metal;
      this.querySelectorAll('[data-gallery-thumb]').forEach((thumb) => {
        const photo = this.resolvePhoto(color, thumb.dataset.galleryThumb, false);
        if (photo.source) thumb.src = this.sized(photo.source, 120);
        else thumb.removeAttribute('src');
      });
      this.querySelectorAll('[data-gallery-view]').forEach((button) => {
        button.hidden = !this.resolvePhoto(color, button.dataset.galleryView, false).source;
        button.setAttribute('aria-pressed', String(button.dataset.galleryView === view));
      });
      this.querySelectorAll('[data-gallery-color]').forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.galleryColor === color)));
      const isStudio = view === 'front' && Boolean(source);
      this.querySelectorAll('[data-gallery-studio-sample], [data-gallery-studio-note]').forEach((note) => { note.hidden = !isStudio; });
      const lifestyleNote = this.querySelector('[data-gallery-lifestyle-note]');
      if (lifestyleNote) lifestyleNote.hidden = !source || view === 'front';
      const sample = isStudio && this.dataset.sampleCarat ? `, ${this.dataset.sampleCarat} carat studio sample` : '';
      this.querySelector('[data-gallery-status]').textContent = source ? `${isReference ? `Selected: ${metal}. Pictured: ${picturedMetal}` : metal}, ${label}${sample}.` : `Photo unavailable in ${metal}.`;
      this.dispatchEvent(new CustomEvent('fere:gallery:render', {
        bubbles: true,
        detail: { color, picturedColor, view, isReference, available: Boolean(source) }
      }));
    }

    openZoom() {
      const picturedColor = this.dataset.picturedColor || this.dataset.color;
      if (!this.photos[picturedColor]?.[this.dataset.view]) return;
      const photo = this.querySelector('[data-gallery-enlarged]');
      photo.src = this.photos[picturedColor][this.dataset.view];
      this.dialog.setAttribute('aria-label', this.picture.alt);
      photo.alt = this.picture.alt;
      this.dialog.showModal();
    }
  });
}

